import { useEffect, useRef } from 'react';

/**
 * Keep a screen current without a manual refresh: calls `refresh` every `intervalMs` while the tab is
 * visible, and right away when the tab becomes visible / the window regains focus.
 * `refresh` must be silent (no spinner, keep filters, pagination and open forms); it is read through a
 * ref, so passing a new function each render does not restart the timer.
 */
export default function useLiveRefresh(refresh, { intervalMs = 15000, enabled = true } = {}) {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (!enabled) return undefined;
    let lastRun = Date.now();
    let running = false;
    const run = async () => {
      if (document.hidden || running) return;
      // focus and visibilitychange usually fire together; one refresh is enough
      if (Date.now() - lastRun < 2000) return;
      lastRun = Date.now();
      running = true;
      try {
        await refreshRef.current();
      } catch (err) {
        // a failed background refresh keeps the data already on screen
      } finally {
        running = false;
      }
    };
    const timer = setInterval(run, intervalMs);
    document.addEventListener('visibilitychange', run);
    window.addEventListener('focus', run);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', run);
      window.removeEventListener('focus', run);
    };
  }, [enabled, intervalMs]);
}
